/* StakeholderPowerInterestGrid — parses the markdown output of the
 * stakeholder_map section and plots each stakeholder on a 2x2 Power-Interest grid.
 *
 * Parsing strategy (robust, no prompt changes required):
 *   1. Look for any markdown table whose header mentions both "power" and "interest".
 *   2. Each row must have a stakeholder name + power + interest rating/quadrant.
 *   3. Ratings can be "HIGH/MED/LOW", numeric (1-10), or quadrant-name keywords
 *      ("Manage Closely"|"Keep Satisfied"|"Keep Informed"|"Monitor").
 *   4. If no valid table is found we render nothing (graceful fallback to markdown).
 *
 * The component is drop-in: parent passes the raw markdown string.
 */
import React, { useMemo, useState } from 'react';
import { Users, AlertCircle } from 'lucide-react';

const GROUP_COLORS = {
  government:    '#0891b2',
  multilateral:  '#7c3aed',
  dfi:           '#7c3aed',
  private:       '#10b981',
  corporate:     '#10b981',
  civil:         '#f59e0b',
  ngo:           '#f59e0b',
  civilsociety:  '#f59e0b',
  external:      '#ec4899',
  media:         '#ec4899',
  default:       '#64748b',
};

function colorFor(nameOrGroup = '') {
  const s = (nameOrGroup || '').toLowerCase();
  for (const [k, v] of Object.entries(GROUP_COLORS)) {
    if (k !== 'default' && s.includes(k)) return v;
  }
  return GROUP_COLORS.default;
}

// Map a free-form power/interest rating to 0-1
function ratingToScore(raw = '') {
  const s = String(raw).toLowerCase().trim();
  if (!s) return null;
  // Numeric 1-10 scale
  const numMatch = s.match(/(\d+(?:\.\d+)?)/);
  if (numMatch) {
    const n = parseFloat(numMatch[1]);
    if (n >= 0 && n <= 10) return Math.min(Math.max(n / 10, 0), 1);
  }
  // Categorical
  if (s.includes('very high') || s.includes('critical') || s === 'high+') return 0.95;
  if (s.includes('high'))                                                 return 0.80;
  if (s.includes('medium-high') || s.includes('med-high'))                return 0.65;
  if (s.includes('medium') || s.includes('mid') || s === 'med')           return 0.50;
  if (s.includes('low-med') || s.includes('medium-low'))                  return 0.35;
  if (s.includes('low') && !s.includes('low-'))                           return 0.20;
  return null;
}

// Quadrant-name mapping (many stakeholder tables label quadrants directly)
function quadrantToCoords(raw = '') {
  const s = String(raw).toLowerCase();
  if (s.includes('manage closely'))  return { p: 0.85, i: 0.85 };
  if (s.includes('keep satisfied'))  return { p: 0.85, i: 0.25 };
  if (s.includes('keep informed'))   return { p: 0.25, i: 0.85 };
  if (s.includes('monitor'))         return { p: 0.20, i: 0.20 };
  return null;
}

function parseStakeholders(markdown = '') {
  if (!markdown || typeof markdown !== 'string') return [];
  const lines = markdown.split('\n');
  const out = [];
  let inTable = false;
  let header = null;
  let powerCol = -1, interestCol = -1, nameCol = -1, groupCol = -1, quadrantCol = -1, roleCol = -1;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line.startsWith('|')) { inTable = false; header = null; continue; }

    // Split cells
    const cells = line.split('|').map(c => c.trim()).filter((_, idx, arr) => idx !== 0 && idx !== arr.length - 1);
    // Header row?
    if (!inTable) {
      const low = cells.map(c => c.toLowerCase());
      if (low.some(c => c.includes('power')) && (low.some(c => c.includes('interest')) || low.some(c => c.includes('quadrant')))) {
        header = low;
        powerCol    = header.findIndex(c => c.includes('power'));
        interestCol = header.findIndex(c => c.includes('interest'));
        quadrantCol = header.findIndex(c => c.includes('quadrant'));
        nameCol     = header.findIndex(c => c.includes('stakeholder') || c.includes('name') || c.includes('actor'));
        groupCol    = header.findIndex(c => c.includes('group') || c.includes('category') || c.includes('type'));
        roleCol     = header.findIndex(c => c.includes('role') || c.includes('position') || c.includes('title'));
        if (nameCol === -1) nameCol = 0;
        inTable = true;
        continue;
      }
    } else {
      // Separator row (|---|---| etc.)
      if (cells.every(c => /^:?-+:?$/.test(c))) continue;
      if (cells.length < 2) continue;

      const name = cells[nameCol] || cells[0];
      if (!name || name.length < 2) continue;

      let p = powerCol    >= 0 ? ratingToScore(cells[powerCol])    : null;
      let iv = interestCol >= 0 ? ratingToScore(cells[interestCol]) : null;
      if ((p === null || iv === null) && quadrantCol >= 0) {
        const q = quadrantToCoords(cells[quadrantCol]);
        if (q) { p = p ?? q.p; iv = iv ?? q.i; }
      }
      if (p === null || iv === null) continue;

      out.push({
        name: name.replace(/\*\*/g, '').replace(/^\*\s+/, '').trim(),
        power: p,
        interest: iv,
        group: groupCol >= 0 ? (cells[groupCol] || '') : '',
        role:  roleCol  >= 0 ? (cells[roleCol]  || '') : '',
      });
    }
  }
  return out;
}

// De-overlap jitter — prevents identical-coordinate dots stacking invisibly
function jitterOverlaps(pts) {
  const seen = new Map();
  return pts.map(p => {
    const key = `${Math.round(p.power * 20)}:${Math.round(p.interest * 20)}`;
    const n = seen.get(key) || 0;
    seen.set(key, n + 1);
    if (n === 0) return p;
    const angle = (n * 2.4) % (Math.PI * 2);
    return {
      ...p,
      power:    Math.min(Math.max(p.power    + Math.cos(angle) * 0.025, 0.02), 0.98),
      interest: Math.min(Math.max(p.interest + Math.sin(angle) * 0.025, 0.02), 0.98),
    };
  });
}

export default function StakeholderPowerInterestGrid({ markdown, darkMode = false }) {
  const [hover, setHover] = useState(null);
  const stakeholders = useMemo(() => jitterOverlaps(parseStakeholders(markdown)), [markdown]);

  if (stakeholders.length < 3) {
    // Not enough to bother rendering — parent falls back to markdown
    return null;
  }

  const W = 720, H = 540, PAD_L = 70, PAD_R = 30, PAD_T = 40, PAD_B = 60;
  const plotW = W - PAD_L - PAD_R;
  const plotH = H - PAD_T - PAD_B;
  const xFor = v => PAD_L + v * plotW;
  const yFor = v => PAD_T + (1 - v) * plotH;

  const bg = darkMode ? '#10151f' : '#ffffff';
  const axis = darkMode ? 'rgba(255,255,255,.25)' : 'rgba(15,23,42,.25)';
  const label = darkMode ? 'rgba(255,255,255,.7)' : 'rgba(15,23,42,.7)';
  const subtle = darkMode ? 'rgba(255,255,255,.12)' : 'rgba(15,23,42,.08)';

  return (
    <div
      data-testid="stakeholder-grid"
      className={`my-6 rounded-xl overflow-hidden border ${darkMode ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200'} shadow-sm`}
    >
      <div className="px-5 pt-4 pb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#7c3aed] to-[#ec4899] flex items-center justify-center">
            <Users className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className={`text-[10px] font-black uppercase tracking-[0.2em] ${darkMode ? 'text-white/50' : 'text-slate-500'}`}>Stakeholder Map · Auto-extracted</p>
            <p className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>{stakeholders.length} stakeholders plotted</p>
          </div>
        </div>
        <span className={`text-[10px] ${darkMode ? 'text-white/40' : 'text-slate-400'}`}>parsed from the markdown table</span>
      </div>

      <div className="p-5 pt-0">
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', background: bg }}>
          {/* Quadrant fills */}
          <rect x={PAD_L} y={PAD_T} width={plotW / 2} height={plotH / 2}
                fill={darkMode ? 'rgba(124,58,237,.08)' : 'rgba(124,58,237,.05)'} />
          <rect x={PAD_L + plotW / 2} y={PAD_T} width={plotW / 2} height={plotH / 2}
                fill={darkMode ? 'rgba(239,68,68,.10)' : 'rgba(239,68,68,.06)'} />
          <rect x={PAD_L} y={PAD_T + plotH / 2} width={plotW / 2} height={plotH / 2}
                fill={darkMode ? 'rgba(100,116,139,.08)' : 'rgba(100,116,139,.04)'} />
          <rect x={PAD_L + plotW / 2} y={PAD_T + plotH / 2} width={plotW / 2} height={plotH / 2}
                fill={darkMode ? 'rgba(16,185,129,.10)' : 'rgba(16,185,129,.06)'} />

          {/* Grid lines */}
          {[0.25, 0.5, 0.75].map(v => (
            <g key={v}>
              <line x1={xFor(v)} y1={PAD_T} x2={xFor(v)} y2={PAD_T + plotH} stroke={subtle} strokeDasharray="2 3" />
              <line x1={PAD_L} y1={yFor(v)} x2={PAD_L + plotW} y2={yFor(v)} stroke={subtle} strokeDasharray="2 3" />
            </g>
          ))}

          {/* Axes */}
          <line x1={PAD_L} y1={PAD_T + plotH} x2={PAD_L + plotW} y2={PAD_T + plotH} stroke={axis} strokeWidth={1.5} />
          <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={PAD_T + plotH} stroke={axis} strokeWidth={1.5} />

          {/* Quadrant labels */}
          <text x={xFor(0.25)} y={yFor(0.92)} fill="#7c3aed" fontSize="10" fontWeight="900" textAnchor="middle" style={{ letterSpacing: '.1em' }}>KEEP INFORMED</text>
          <text x={xFor(0.75)} y={yFor(0.92)} fill="#ef4444" fontSize="10" fontWeight="900" textAnchor="middle" style={{ letterSpacing: '.1em' }}>MANAGE CLOSELY</text>
          <text x={xFor(0.25)} y={yFor(0.08)} fill="#64748b" fontSize="10" fontWeight="900" textAnchor="middle" style={{ letterSpacing: '.1em' }}>MONITOR</text>
          <text x={xFor(0.75)} y={yFor(0.08)} fill="#10b981" fontSize="10" fontWeight="900" textAnchor="middle" style={{ letterSpacing: '.1em' }}>KEEP SATISFIED</text>

          {/* Axis labels */}
          <text x={PAD_L + plotW / 2} y={H - 18} fill={label} fontSize="12" fontWeight="700" textAnchor="middle">INTEREST →</text>
          <text x={18} y={PAD_T + plotH / 2} fill={label} fontSize="12" fontWeight="700" textAnchor="middle"
                transform={`rotate(-90, 18, ${PAD_T + plotH / 2})`}>POWER →</text>

          {/* Dots */}
          {stakeholders.map((s, i) => {
            const c = colorFor(s.group || s.name);
            const isHover = hover === i;
            return (
              <g key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} style={{ cursor: 'pointer' }}>
                <circle cx={xFor(s.interest)} cy={yFor(s.power)} r={isHover ? 9 : 6.5}
                        fill={c} fillOpacity={0.85} stroke="white" strokeWidth={1.5} />
                {isHover && (
                  <>
                    <rect
                      x={xFor(s.interest) + 12}
                      y={yFor(s.power) - 22}
                      width={Math.min(Math.max(s.name.length * 6.5 + 20, 100), 260)}
                      height={36}
                      rx={4}
                      fill={darkMode ? '#0b1c30' : '#0b1c30'}
                    />
                    <text x={xFor(s.interest) + 20} y={yFor(s.power) - 7} fill="white" fontSize="11" fontWeight="700">
                      {s.name.slice(0, 32)}
                    </text>
                    <text x={xFor(s.interest) + 20} y={yFor(s.power) + 6} fill="rgba(255,255,255,.7)" fontSize="9">
                      P {(s.power * 10).toFixed(1)} · I {(s.interest * 10).toFixed(1)} {s.group ? `· ${s.group.slice(0, 20)}` : ''}
                    </text>
                  </>
                )}
              </g>
            );
          })}
        </svg>

        {/* Group legend */}
        <div className="flex items-center flex-wrap gap-3 mt-3 px-2">
          {Array.from(new Set(stakeholders.map(s => (s.group || '').split(/[·,/]/)[0].trim()).filter(Boolean))).slice(0, 6).map(g => (
            <div key={g} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: colorFor(g) }} />
              <span className={`text-[10.5px] ${darkMode ? 'text-white/60' : 'text-slate-500'}`}>{g}</span>
            </div>
          ))}
        </div>
        <div className={`text-[10px] mt-2 flex items-center gap-1.5 ${darkMode ? 'text-white/40' : 'text-slate-400'}`}>
          <AlertCircle className="w-3 h-3" /> Coordinates parsed from the Stakeholder table below · hover a dot for details
        </div>
      </div>
    </div>
  );
}
